import {
  makeStyles,
  tokens,
  Text,
  Checkbox,
  Badge,
  Card,
} from '@fluentui/react-components';
import { Checkmark24Filled, Add24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { EmployeeUpdateFormData } from '../../../hooks/useEmployeeUpdateForm';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  card: {
    padding: tokens.spacingVerticalM,
    cursor: 'pointer',
    position: 'relative',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  cardContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  cardText: {
    flex: 1,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXS,
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
    flexWrap: 'wrap',
  },
  currentBadge: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  addBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  removeBadge: {
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
});

interface AccessGroup {
  id: string;
  name: string;
  description: string;
}

// Define available access groups (matches backend GROUP_MAPPINGS)
const ACCESS_GROUPS: AccessGroup[] = [
  {
    id: import.meta.env.VITE_GROUP_VPN || '',
    name: 'VPN Access',
    description: 'Remote access to company network',
  },
  {
    id: import.meta.env.VITE_GROUP_SAGE || '',
    name: 'SAGE ERP',
    description: 'Access to SAGE accounting software',
  },
  {
    id: import.meta.env.VITE_GROUP_ONBOARDING_USERS || '',
    name: 'HR Onboarding System',
    description: 'Access to HR self-service portal',
  },
  // Add HubSpot and FileServer when environment variables are available
];

interface AccessStepProps {
  formData: EmployeeUpdateFormData;
  updateFormData: (updates: Partial<EmployeeUpdateFormData>) => void;
}

export function AccessStep({ formData, updateFormData }: AccessStepProps) {
  const styles = useStyles();

  const toggleGroup = (groupId: string) => {
    const isSelected = formData.selectedGroups.includes(groupId);
    const newSelectedGroups = isSelected
      ? formData.selectedGroups.filter((id) => id !== groupId)
      : [...formData.selectedGroups, groupId];

    updateFormData({ selectedGroups: newSelectedGroups });
  };

  const isGroupSelected = (groupId: string) => formData.selectedGroups.includes(groupId);
  const isGroupOriginal = (groupId: string) => formData.originalGroups.includes(groupId);

  const getGroupStatus = (groupId: string) => {
    const selected = isGroupSelected(groupId);
    const original = isGroupOriginal(groupId);

    if (selected && original) return 'current';
    if (selected && !original) return 'will-add';
    if (!selected && original) return 'will-remove';
    return 'none';
  };

  return (
    <div className={styles.container}>
      <Text size={500} weight="semibold">
        Access Provisioning
      </Text>
      <Text>
        Manage group memberships for the employee. Check the boxes to add groups, uncheck to remove them.
      </Text>

      <div className={styles.grid}>
        {ACCESS_GROUPS.filter((group) => group.id).map((group) => {
          const status = getGroupStatus(group.id);
          const isChecked = isGroupSelected(group.id);

          return (
            <Card
              key={group.id}
              className={styles.card}
              onClick={() => toggleGroup(group.id)}
            >
              <div className={styles.cardContent}>
                <Checkbox checked={isChecked} />
                <div className={styles.cardText}>
                  <Text weight="semibold">{group.name}</Text>
                  <div className={styles.description}>{group.description}</div>
                  <div className={styles.badges}>
                    {status === 'current' && (
                      <Badge
                        appearance="filled"
                        color="informative"
                        icon={<Checkmark24Filled />}
                        className={styles.currentBadge}
                      >
                        Current
                      </Badge>
                    )}
                    {status === 'will-add' && (
                      <Badge
                        appearance="filled"
                        color="success"
                        icon={<Add24Regular />}
                        className={styles.addBadge}
                      >
                        Will Add
                      </Badge>
                    )}
                    {status === 'will-remove' && (
                      <Badge
                        appearance="filled"
                        color="danger"
                        icon={<Dismiss24Regular />}
                        className={styles.removeBadge}
                      >
                        Will Remove
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {ACCESS_GROUPS.filter((group) => group.id).length === 0 && (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          No access groups configured. Please set environment variables for group IDs.
        </Text>
      )}
    </div>
  );
}
